<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-chart-network'"
      :section-title="$tc('configuration.sections.caseTypes', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-testing="'activate'"
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'deactivate'"
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-testing="'delete'"
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteCaseTypes(selectedRows)"
        @shortkey="deleteCaseTypes(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'update'"
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCaseTypesModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showCaseTypesModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'add'"
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCaseTypesModal($t('configuration.actions.add'))"
        @shortkey="showCaseTypesModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="caseTypesModal">
      <caseTypes-modal
        :action-title="actionTitle"
        @update="patchCaseTypes($event)"
        @create="createCaseType($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import CaseTypesModal from '@/modules/configurationModule/detail/cases/caseTypes/CaseTypesModal'
import columnsCaseTypesGrid from '@/modules/configurationModule/detail/cases/caseTypes/columnsCaseTypesGrid'
import CaseTypesConfigurationServices from '@/api/resources/configuration/cases/caseType/CaseTypesConfigurationServices'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'

export default {
    name: 'CasesCaseTypesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        CaseTypesModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.cases.caseTypes',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsCaseTypesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveCaseTypes() {
            return !!this.selectedRows.find(caseType => caseType.active)
        },
        hasDeactiveCaseTypes() {
            return !!this.selectedRows.find(caseType => !caseType.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(caseType => caseType.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(caseType => !caseType.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveCaseTypes && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveCaseTypes && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const caseTypesToPatch =
                selectionBatch
                    .filter(caseType => caseType.active !== option)
                    .map(caseType => ({
                        active: option,
                        id: caseType.id
                    }))
            if (caseTypesToPatch.length) {
                const response =
                    await this.$_toCallUnique(
                        CaseTypesConfigurationServices.patchCaseTypesBatch(caseTypesToPatch))
                if (response !== null) {
                    this.$_showToastSuccess(
                        this.$tc(
                            'configuration.toastMessages.updated',
                            caseTypesToPatch.length,
                            { elements: caseTypesToPatch.length }))
                    this.reloadData()
                }
            }
        },
        async patchCaseTypes(...caseTypes) {
            const caseTypePatchInBatchRequests =
                caseTypes.map(caseType => ({
                    active: caseType.active,
                    description: caseType.description,
                    id: caseType.id
                }))
            const response =
                await this.$_toCallUnique(
                    CaseTypesConfigurationServices.patchCaseTypesBatch(caseTypePatchInBatchRequests))
            if (response !== null) {
                this.$refs.caseTypesModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                this.reloadData()
            }
        },
        async createCaseType(caseType) {
            const caseTypeCreateRequest = {
                active: caseType.active,
                description: caseType.description
            }
            const response =
                await this.$_toCallUnique(
                    CaseTypesConfigurationServices.createCaseType(caseTypeCreateRequest))
            if (response !== null) {
                this.$refs.caseTypesModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteCaseTypesBatch(caseTypes) {
            const caseTypesToDelete = caseTypes.map(caseType => caseType.id)
            const response =
                await this.$_toCallUnique(
                    CaseTypesConfigurationServices.deleteCaseTypesBatch(caseTypesToDelete))
            if (response !== null) {
                this.$_showToastSuccess(
                    this.$tc(
                        'configuration.toastMessages.deleted',
                        caseTypesToDelete.length,
                        { elements: caseTypesToDelete.length }))
                this.reloadData()
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(CaseTypesConfigurationServices))
        },
        getContextMenuItems(params) {
            if (params?.node) {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showCaseTypesModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteCaseTypes(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
            return []
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(CaseTypesConfigurationServices))
        },
        showCaseTypesModal(title, selectedCaseTypes) {
            this.actionTitle = title
            this.$refs.caseTypesModal.show([selectedCaseTypes])
        },
        async deleteCaseTypes(caseTypes) {
            if (caseTypes.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteCaseTypesBatch(caseTypes)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
